import {
  Box,
  Button,
  Flex,
  Text,
  Menu,
  Table,
  Badge,
  Avatar,
  Spinner,
  HStack
} from '@chakra-ui/react';
import { DotsThree, Users } from '@phosphor-icons/react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import InviteMemberDialog from './InviteMemberDialog';
import { apiGetTeam, GetTeamResponse } from '@/api/services';
import { AccountRole } from '@/types/enums';
import { fetchAPI } from '@/api/fetch';
import { useTeamManagement } from '@/hooks/useTeamManagement';
import { ConfirmationDialog } from '@/components/dialogs/ConfirmationDialog';
import { TeamMember, ConfirmationDialogState, ConfirmationDialogType } from '@/types/team';

const TABLE_COLUMNS = [
  { field: 'name', header: 'Name' },
  { field: 'email', header: 'Email' },
  { field: 'role', header: 'Role' },
  { field: 'status', header: 'Status' },
  { field: 'actions', header: '' }
] as const;

interface TeamProps {
  account: Account | null;
}

export default function Team({ account }: TeamProps) {
  const [currentMembers, setCurrentMembers] = useState<number>(0);
  const [maxMembers, setMaxMembers] = useState<number>(0);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [confirmationDialog, setConfirmationDialog] = useState<ConfirmationDialogState>({
    isOpen: false,
    type: 'resend',
    member: null,
    updatedRole: null,
    isLoading: false,
    error: null
  });

  const {
    handleInviteMember,
    handleResendInvitation,
    handleRemoveInvitation,
    handleRemoveMember,
    handleUpdateRole
  } = useTeamManagement({
    setTeamMembers,
    setConfirmationDialog,
    closeConfirmationDialog: () => setConfirmationDialog((prev) => ({ ...prev, isOpen: false }))
  });

  useEffect(() => {
    setIsLoading(account === null);
  }, [account]);

  const fetchTeamMembers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchAPI<GetTeamResponse>({
        request: () => apiGetTeam()
      });
      if (response.success) {
        const { team_members, invitations, max_members, current_members } = response.data;
        setMaxMembers(max_members);
        setCurrentMembers(current_members);
        const members: TeamMember[] = [
          ...team_members.map((account) => ({
            account_id: account.account_id,
            name: account.name,
            email: account.email,
            role: account.role,
            status: 'active' as const
          })),
          ...invitations.map((invitation) => ({
            account_id: null,
            name: invitation.email.split('@')[0],
            email: invitation.email,
            role: invitation.role,
            status: 'invited' as const
          }))
        ];
        setTeamMembers(members);
      } else {
        console.error('Failed to fetch team members', response.message);
      }
    } catch {
      console.error('Failed to fetch team members');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  const openConfirmationDialog = useCallback(
    (type: ConfirmationDialogType, member: TeamMember, updatedRole: AccountRole | null = null) => {
      setConfirmationDialog({
        isOpen: true,
        type,
        member,
        updatedRole,
        isLoading: false,
        error: null
      });
    },
    []
  );

  const handleConfirmAction = useCallback(async () => {
    if (!confirmationDialog.member) return;

    switch (confirmationDialog.type) {
      case 'remove-invitation':
        await handleRemoveInvitation(confirmationDialog.member.email);
        break;
      case 'remove-member':
        await handleRemoveMember(confirmationDialog.member.account_id!);
        break;
      case 'resend':
        await handleResendInvitation(
          confirmationDialog.member.email,
          confirmationDialog.member.role
        );
        break;
      case 'changeRole':
        if (!confirmationDialog.updatedRole) return;
        await handleUpdateRole(confirmationDialog.updatedRole);
        break;
    }
  }, [
    confirmationDialog,
    handleRemoveInvitation,
    handleRemoveMember,
    handleResendInvitation,
    handleUpdateRole
  ]);

  const getConfirmationDialogProps = useCallback(() => {
    if (!confirmationDialog.member) return null;

    const newRole = confirmationDialog.member.role === AccountRole.ADMIN ? 'Member' : 'Admin';

    switch (confirmationDialog.type) {
      case 'remove-invitation':
        return {
          title: 'Remove Invitation',
          description: `Are you sure you want to remove the invitation for ${confirmationDialog.member.email}? This action cannot be undone.`,
          confirmText: 'Remove'
        };
      case 'remove-member':
        return {
          title: 'Remove Member',
          description: `Are you sure you want to remove ${confirmationDialog.member.name} from the team? This action cannot be undone.`,
          confirmText: 'Remove'
        };
      case 'resend':
        return {
          title: 'Resend Invitation',
          description: `Are you sure you want to resend the invitation to ${confirmationDialog.member.email}?`,
          confirmText: 'Resend'
        };
      case 'changeRole':
        return {
          title: 'Change Role',
          description: `Are you sure you want to change ${confirmationDialog.member.name}'s role to ${newRole}?`,
          confirmText: 'Change'
        };
      default:
        return null;
    }
  }, [confirmationDialog]);

  const renderCellContent = useCallback(
    (member: TeamMember, field: string) => {
      switch (field) {
        case 'name':
          if (member.status === 'active') {
            return (
              <Flex align="center" gap={3}>
                <Avatar.Root size="md">
                  <Avatar.Image src={member.avatar} />
                  <Avatar.Fallback>{member.name[0]}</Avatar.Fallback>
                </Avatar.Root>
                <Box>
                  <Text fontSize="16px">{member.name}</Text>
                </Box>
              </Flex>
            );
          }
          return <Box />;
        case 'email':
          return (
            <Text
              color={member.status === 'active' ? 'neutrals.700' : 'neutrals.500'}
              fontSize="16px"
            >
              {member.email}
            </Text>
          );
        case 'role':
          return (
            <Badge
              color={member.role === AccountRole.ADMIN ? 'primary.900' : 'neutrals.700'}
              bg={member.role === AccountRole.ADMIN ? 'primary.100' : 'neutrals.100'}
              px={2}
              py={1}
              borderRadius="full"
              fontSize="14px"
            >
              {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
            </Badge>
          );
        case 'status':
          return (
            <Badge
              color={member.status === 'active' ? 'primary.900' : 'neutrals.700'}
              bg={member.status === 'active' ? 'primary.100' : 'neutrals.100'}
              px={2}
              py={1}
              borderRadius="full"
              fontSize="14px"
            >
              {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
            </Badge>
          );
        case 'actions':
          if (account?.role === AccountRole.ADMIN && member.account_id !== account?.account_id) {
            return (
              <Menu.Root>
                <Menu.Trigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    p="8px"
                    minW="auto"
                    h="auto"
                    borderRadius="full"
                    _hover={{ bg: 'neutrals.100' }}
                  >
                    <DotsThree size={24} weight="bold" />
                  </Button>
                </Menu.Trigger>
                <Menu.Positioner>
                  <Menu.Content minW="10rem" zIndex={1500}>
                    {member.status === 'active' && (
                      <>
                        {member.role === AccountRole.ADMIN && (
                          <Menu.Item
                            value={AccountRole.MEMBER}
                            fontSize="14px"
                            _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                            onClick={() =>
                              openConfirmationDialog('changeRole', member, AccountRole.MEMBER)
                            }
                          >
                            Change to Member
                          </Menu.Item>
                        )}
                        {member.role === AccountRole.MEMBER && (
                          <Menu.Item
                            value={AccountRole.ADMIN}
                            fontSize="14px"
                            _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                            onClick={() =>
                              openConfirmationDialog('changeRole', member, AccountRole.ADMIN)
                            }
                          >
                            Change to Admin
                          </Menu.Item>
                        )}
                        {member.account_id !== account?.account_id && (
                          <Menu.Item
                            value="remove"
                            fontSize="14px"
                            color="error.500"
                            _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                            onClick={() => openConfirmationDialog('remove-member', member)}
                          >
                            Remove Member
                          </Menu.Item>
                        )}
                      </>
                    )}

                    {member.status === 'invited' && (
                      <>
                        <Menu.Item
                          value="resend"
                          fontSize="14px"
                          color="neutrals.700"
                          _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                          onClick={() => openConfirmationDialog('resend', member)}
                        >
                          Resend Invitation
                        </Menu.Item>
                        <Menu.Item
                          value="remove"
                          fontSize="14px"
                          color="error.500"
                          _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                          onClick={() => openConfirmationDialog('remove-invitation', member)}
                        >
                          Remove Invitation
                        </Menu.Item>
                      </>
                    )}
                  </Menu.Content>
                </Menu.Positioner>
              </Menu.Root>
            );
          }
          return <Box />;
        default:
          return null;
      }
    },
    [account, openConfirmationDialog]
  );

  const tableContent = useMemo(() => {
    if (isLoading) {
      return (
        <Table.Row>
          <Table.Cell colSpan={TABLE_COLUMNS.length} textAlign="center" py={8}>
            <Spinner size="lg" color="primary.900" borderWidth="3px" />
          </Table.Cell>
        </Table.Row>
      );
    }

    if (teamMembers.length === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan={TABLE_COLUMNS.length} textAlign="center" py={8}>
            <Text color="neutrals.700">No team members found</Text>
          </Table.Cell>
        </Table.Row>
      );
    }

    return teamMembers.map((member) => (
      <Table.Row
        key={member.email}
        _hover={{ bg: 'neutrals.50' }}
        transition="background-color 0.2s"
      >
        {TABLE_COLUMNS.map((column) => (
          <Table.Cell key={`${member.email}-${column.field}`} py={3} px={4}>
            {renderCellContent(member, column.field)}
          </Table.Cell>
        ))}
      </Table.Row>
    ));
  }, [isLoading, teamMembers, renderCellContent]);

  const isAtCapacity = currentMembers >= maxMembers;

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="24px" fontWeight="bold" mb={2}>
            Team Members
          </Text>
          <HStack gap={3} align="center">
            <Flex align="center" gap={2}>
              <Users size={20} weight="duotone" />
              <Text fontSize="14px" color="neutrals.700">
                {currentMembers} / {maxMembers} members
              </Text>
            </Flex>
            {isAtCapacity && (
              <Badge
                variant="subtle"
                p="4px 8px"
                borderRadius="full"
                fontSize="12px"
                bg="warning.100"
                color="warning.700"
                border="1px solid"
                borderColor="neutrals.200"
              >
                At Capacity
              </Badge>
            )}
          </HStack>
        </Box>
        <InviteMemberDialog onInvite={handleInviteMember} disabled={isAtCapacity} />
      </Flex>

      <Table.Root>
        <Table.Header>
          <Table.Row>
            {TABLE_COLUMNS.map((column) => (
              <Table.Cell key={column.field} bg="neutrals.100" py={3} px={4}>
                <Text fontWeight="medium" color="neutrals.700" fontSize="16px">
                  {column.header}
                </Text>
              </Table.Cell>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>{tableContent}</Table.Body>
      </Table.Root>

      {confirmationDialog.isOpen && confirmationDialog.member && (
        <ConfirmationDialog
          isOpen={confirmationDialog.isOpen}
          onClose={() => setConfirmationDialog((prev) => ({ ...prev, isOpen: false }))}
          onConfirm={handleConfirmAction}
          isLoading={confirmationDialog.isLoading}
          error={confirmationDialog.error}
          {...getConfirmationDialogProps()!}
        />
      )}
    </Box>
  );
}
